// Orders (sales history) for the Fashion layout. Core's searchOrderHistory always
// pins a plain listing to this till (BUG-2100), so on a store with several tills
// Orders opened empty. Fashion builds its own salesHistory query: every till by
// default, with the till as a filter the cashier picks.
import dayjs from "dayjs";
import { isCancelledOfdStatus, ofdStatusRank } from "../aggregatorOrderState";

export const ORDERS_PAGE = 50;

export const ORDER_RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "Last 7 days" },
  { key: "month", label: "This month" },
];

export const PENDING_SYNC = "Pending sync";
export const ORDER_STATUSES = ["All", "Completed", "Returned", "Cancelled", PENDING_SYNC];

// "All tills" sends tillId null; "this" sends this till's id; any other value is
// a till name picked from the loaded rows and is filtered on the client.
export const ALL_TILLS = "all";
export const THIS_TILL = "this";

const DAY = "YYYY-MM-DD";

export function dateRangeFor(key, now = dayjs()) {
  const today = dayjs(now);
  if (key === "yesterday") {
    const y = today.subtract(1, "day");
    return { from: y.format(DAY), to: y.format(DAY) };
  }
  if (key === "last7") return { from: today.subtract(6, "day").format(DAY), to: today.format(DAY) };
  if (key === "month") return { from: today.startOf("month").format(DAY), to: today.format(DAY) };
  return { from: today.format(DAY), to: today.format(DAY) };
}

// Keeps a typed term from breaking out of the GraphQL string.
export const cleanTerm = (s) => String(s || "").replace(/["\\]/g, "").trim();

// Digits of a typed mobile, without the country code ("+91 98765" -> "98765").
function mobileDigits(term) {
  const t = String(term || "").trim();
  const digits = t.replace(/\D/g, "");
  if (t.startsWith("+91") || (digits.length === 12 && digits.startsWith("91"))) return digits.slice(2);
  return digits;
}

// A typed mobile number: digits only, and an Indian 12-digit number loses its 91.
// Anything that is not a phone number is returned cleaned but unchanged.
export function normaliseSearch(term) {
  const t = cleanTerm(term);
  if (!/^\+?[\d\s-]+$/.test(t)) return t;
  const digits = mobileDigits(t);
  return digits.length < 6 ? t : digits;
}

const gqlString = (value) => (value == null || value === "" ? "null" : JSON.stringify(String(value)));

// tillId defaults to null: the server then returns every till's orders, kept to
// this store by storeId (filter_by cSBunitID). With q set the server searches bill
// no, invoice no, customer name/code/mobile across all dates.
export function buildSalesHistoryQuery({ from, to, tillId = null, storeId = null, q = "", startRow = 0, endRow = startRow + ORDERS_PAGE, layAway = "N" } = {}) {
  const filter = {};
  if (storeId) filter.cSBunitID = String(storeId);
  if (from && to) filter.DateRange = [from, to];
  const filterBy = Object.keys(filter).length ? JSON.stringify(JSON.stringify(filter)) : "null";
  const sortBy = JSON.stringify(JSON.stringify({ documentno: "desc", dateordered: "desc" }));
  const term = normaliseSearch(q);
  return {
    query: `query {salesHistory(
      q: ${gqlString(term)},
      filter_by: ${filterBy},
      startRow: "${Number(startRow) || 0}",
      endRow: "${Number(endRow) || ORDERS_PAGE}",
      sort_by: ${sortBy},
      tillId: ${gqlString(tillId)},
      layAway: "${layAway === "Y" ? "Y" : "N"}"
    ) {
      sOrderID documentno invoiceNo invoiceId invoiceStatus invoiceDate dateordered totalQty orderTime grosstotal
      layAway isReturn docstatus creditSale estimateDocumentNo deliveryDate ofdStatus
      saleType { cwrSaletypeId name value }
      cwrB2cCustomer { cwrCustomerId code name mobileNo pincode email retlLoyaltyBalance loyaltyLevelId creditBalance creditApproachPercentage
        loyaltyLevel { name } cwrWallet { wallet_Id balance_Amt }
        sCustomer { sCustomerID customerCategory { sCustomerCategoryId value name description } } }
      cwrTill { till }
      finReceiptPlan { finReceiptPlanDetails { amount cwrPaymentmethod { paymentProvider } } }
      cSBunitID csBUnit { csBunitId name }
    } }`,
  };
}

// A store bill marked to ship carries a live delivery status. Walk-in bills are
// saved as "Delivered", so delivered/completed and cancelled ones aren't shipping.
const DELIVERED_RANK = ofdStatusRank("DL");
export const isBeingShipped = (order) => {
  const status = order?.ofdStatus;
  if (!String(status || "").trim() || isCancelledOfdStatus(status)) return false;
  return ofdStatusRank(status) < DELIVERED_RANK;
};

// Rows of a later page that an earlier page already holds are dropped.
export function mergeOrderPages(prev, page) {
  const seen = new Set(prev.map((r) => r.key));
  return [...prev, ...page.filter((r) => !seen.has(r.key))];
}

export function orderStatus(order) {
  if (order?.docstatus === "CAN" || order?.docstatus === "VO") return "Cancelled";
  if (order?.isReturn === "Y" || order?.isReturn === true) return "Returned";
  return "Completed";
}

// Payment names on a bill, each once, in the order paid ("Cash + UPI").
export function paidByOf(payments) {
  const names = (Array.isArray(payments) ? payments : []).filter((p) => p && Number(p.amount) !== 0).map((p) => String(p.name || "").trim());
  return [...new Set(names.filter(Boolean))].join(" + ");
}

// The server keeps a bill's payments on its receipt plan (SOrder has no
// payments field); Core reads the same block. finReceiptPlan comes back as a
// list or a single object depending on the resolver.
export function receiptPayments(item) {
  const plans = [].concat(item?.finReceiptPlan || []);
  return plans.flatMap((plan) => [].concat(plan?.finReceiptPlanDetails || [])).map((d) => ({ name: d?.cwrPaymentmethod?.paymentProvider || "", amount: Number(d?.amount) || 0 }));
}

// Same row shape Core's order history builds, so the return and receipt helpers
// read it the same way.
export function mapSalesHistoryRow(item, precision = 2) {
  const total = Number(parseFloat(item?.grosstotal || 0).toFixed(precision));
  return {
    sOrderID: item?.sOrderID,
    documentno: item?.documentno,
    key: item?.sOrderID || item?.documentno,
    customer: item?.cwrB2cCustomer || "",
    total,
    originalPrice: item?.grosstotal || 0,
    orderTime: item?.orderTime || "",
    orderDate: item?.dateordered || "",
    totalQty: Number(item?.totalQty) || 0,
    isReturn: item?.isReturn || "N",
    docstatus: item?.docstatus,
    layAway: item?.layAway || "N",
    creditSale: item?.creditSale || "N",
    saleType: item?.saleType || {},
    deliveryDate: item?.deliveryDate,
    estimateDocumentNo: item?.estimateDocumentNo || "",
    invoiceNo: item?.invoiceNo || "",
    invoiceId: item?.invoiceId || "",
    invoiceStatus: item?.invoiceStatus || "",
    invoiceDate: item?.invoiceDate || "",
    tillName: item?.cwrTill?.till || "",
    ofdStatus: item?.ofdStatus || "",
    paidBy: paidByOf(receiptPayments(item)),
    salesHistory: "Y",
    status: orderStatus(item),
  };
}

// Pending advance orders stay out of sales history when pre-orders are on (as Core does).
export function readSalesHistoryRows(response, { precision = 2, posConfig } = {}) {
  const list = response?.data?.data?.salesHistory;
  if (!Array.isArray(list)) return null;
  return list.filter((item) => !(posConfig?.enablePreOrder === "Y" && item?.layAway === "Y" && !item?.invoiceNo)).map((item) => mapSalesHistoryRow(item, precision));
}

// A bill this till saved that hasn't reached the server yet. It is shown read-only:
// the server has no copy to reprint, return or ship from until it syncs. Local
// orders carry "DD-MM-YYYY" dates, turned into ISO so the date range and the
// "Today · 14:05" label read them.
const LOCAL_DAY = /^(\d{2})-(\d{2})-(\d{4})/;
const isoDay = (s) => {
  const m = LOCAL_DAY.exec(String(s || ""));
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
};

export function mapPendingOrder(order, { precision = 2, tillName = "" } = {}) {
  const time = String(order?.orderTime || "").split(" ")[1] || "";
  const total = Number(parseFloat(order?.total || 0).toFixed(precision));
  return {
    sOrderID: order?.sOrderID,
    documentno: order?.documentno,
    key: `local-${order?.sOrderID || order?.documentno}`,
    customer: order?.customer || "",
    total,
    orderTime: time.slice(0, 5),
    orderDate: isoDay(order?.orderDate) || isoDay(order?.orderTime),
    totalQty: Number(order?.totalQty) || 0,
    isReturn: order?.isReturn === true || order?.isReturn === "Y" ? "Y" : "N",
    tillName,
    paidBy: paidByOf(order?.payments),
    pending: true,
    items: order?.items || [],
    paymentData: order?.payments || [],
    status: PENDING_SYNC,
  };
}

// Pending bills inside the date range go on top; any the server already returned are dropped.
export function withPendingOrders(rows, pending, { from, to } = {}) {
  const known = new Set();
  (rows || []).forEach((r) => {
    if (r.sOrderID) known.add(`id:${r.sOrderID}`);
    if (r.documentno) known.add(`no:${r.documentno}`);
  });
  const extra = (pending || []).filter((p) => {
    if ((p.sOrderID && known.has(`id:${p.sOrderID}`)) || (p.documentno && known.has(`no:${p.documentno}`))) return false;
    if (from && to && p.orderDate) return p.orderDate >= from && p.orderDate <= to;
    return true;
  });
  return [...extra, ...(rows || [])];
}

const digitsOf = (s) => String(s || "").replace(/\D/g, "");

// Client-side filters over the loaded page: till name, status, and a search that
// also narrows rows already on screen (bill no, customer name, mobile digits).
export function filterOrders(rows, { till = ALL_TILLS, status = "All", q = "" } = {}) {
  const term = String(q || "").trim().toLowerCase();
  const qd = /^\+?[\d\s-]+$/.test(term) ? mobileDigits(term) : "";
  return (rows || []).filter((o) => {
    if (till !== ALL_TILLS && till !== THIS_TILL && o.tillName !== till) return false;
    if (status !== "All" && o.status !== status) return false;
    if (!term) return true;
    const c = o.customer && typeof o.customer === "object" ? o.customer : { name: o.customer };
    return (
      String(o.documentno || "").toLowerCase().includes(term) ||
      String(o.invoiceNo || "").toLowerCase().includes(term) ||
      String(c.name || "").toLowerCase().includes(term) ||
      (qd.length >= 3 && digitsOf(c.mobileNo).includes(qd))
    );
  });
}

// Bills: every bill that was not cancelled. Net sales: sales less returns.
// Average: net sales over the sale bills.
export function summarizeOrders(rows) {
  const live = (rows || []).filter((o) => o.status !== "Cancelled");
  let sales = 0;
  let returns = 0;
  let saleBills = 0;
  live.forEach((o) => {
    const amt = Math.abs(Number(o.total) || 0);
    if (o.status === "Returned" || (o.pending && o.isReturn === "Y")) returns += amt;
    else {
      sales += amt;
      saleBills += 1;
    }
  });
  const net = sales - returns;
  return { bills: live.length, net, returns, average: saleBills ? net / saleBills : 0 };
}

// Till names seen on the loaded rows, for the till filter.
export function tillNames(rows) {
  return [...new Set((rows || []).map((o) => o.tillName).filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}
